/**
 * Artifact file-format helpers.
 *
 * Artifacts are either markdown/structured (rendered + downloaded client-side)
 * or file-backed (PDF, DOCX, XLSX, …) whose real bytes come from
 * `GET /artifacts/:id/download`. These helpers give every format a consistent
 * icon + label and a single source of truth for "is this a downloadable file",
 * so the pill and the side panel never disagree.
 */

import {
  FileArchive,
  FileCode,
  FileImage,
  FileJson,
  FileSpreadsheet,
  FileText,
  Presentation,
  type LucideIcon,
} from 'lucide-react';

/** Formats that are the markdown source itself — not a separate binary file. */
const MARKDOWN_FORMATS = new Set(['markdown', 'md', '']);

const normalise = (format?: string | null): string => (format ?? '').toLowerCase().trim();

/**
 * A file-backed format is anything other than plain markdown — PDF, Office
 * documents, spreadsheets, images, etc. These must be fetched from the download
 * endpoint rather than saved from the markdown `content`.
 */
export const isDownloadableFileFormat = (format?: string | null): boolean =>
  !MARKDOWN_FORMATS.has(normalise(format));

/** Map a file format / extension to a representative lucide icon. */
const FORMAT_ICONS: Record<string, LucideIcon> = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  rtf: FileText,
  txt: FileText,
  md: FileText,
  markdown: FileText,
  ppt: Presentation,
  pptx: Presentation,
  key: Presentation,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  csv: FileSpreadsheet,
  tsv: FileSpreadsheet,
  json: FileJson,
  html: FileCode,
  htm: FileCode,
  xml: FileCode,
  yaml: FileCode,
  yml: FileCode,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
  svg: FileImage,
  webp: FileImage,
  zip: FileArchive,
  rar: FileArchive,
  '7z': FileArchive,
  tar: FileArchive,
  gz: FileArchive,
};

/** Icon for a given format; falls back to a generic document icon. */
export const getArtifactFileIcon = (format?: string | null): LucideIcon =>
  FORMAT_ICONS[normalise(format)] ?? FileText;

/** Short uppercase label for the format badge (e.g. `PDF`, `DOCX`). */
export const getArtifactFormatLabel = (format?: string | null): string =>
  normalise(format).toUpperCase();
